import React from 'react'
import { connect } from 'react-redux'
import { Accordion } from 'semantic-ui-react'
// Local
import {
  CONDITION,
  HCC_GROUP
} from '../../../constant'
import CdiConditionEdit from './cdiConditionEdit'
import CdiConditionRead from './cdiConditionRead'
import CdiConditionHccHeaderTitle from './cdiConditionHccHeaderTitle'
import { EncounterConditionDisplay } from '../../authenticated/types'
import { ObjectValues } from '../../../types/types'
import { RootState } from '../../../store'

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>


/**
 * Provides a CDI user role component that displays conditions grouped
 * under an HCC
 *
 * @param editMode True if encounter is locked and editing is enabled
 * @param hccCode HCC code of the display group
 * @param hccDescription HCC description corresponding to HCC code
 * @param activeHccGroupId Identifier of currently active HCC group
 * @param conditionGroupTitle The conditionStatus group
 *        Values: KNOWN, POTENTIAL, ACTIVITY
 * @param conditions List of conditions to display in the group
 * @param encounterDate Date of the encounter
 * @param onSubmitNote Callback for condition note save
 *        Arguments: condition, noteContent, noteReason
 * @param onConditionAction Callback for condition action.
 *        Arguments: action, condition
 * @param onHccGroupAction Callback for HCC group action
 *        Arguments: action, hcc-group-id
 */

type Props = ReduxStateProps & {
  activeHccGroupId: any
  conditionGroupTitle: any
  conditions: EncounterConditionDisplay[]
  encounterDate: number
  editMode: boolean
  hccCode: any
  hccDescription: string
  onConditionAction: (
    action: keyof typeof CONDITION.ACTION,
    condition: EncounterConditionDisplay[] | EncounterConditionDisplay | string
  ) => void
  onHccGroupAction: (
    action: ObjectValues<typeof HCC_GROUP.ACTION>,
    hccGroupID: string
  ) => void
  onSubmitNote: any
}

function CdiConditionHccGroup(
  props: Props
): JSX.Element | null {
  const {
    activeHccGroupId,
    conditionGroupTitle,
    conditions,
    encounterDate,
    currentUser,
    editMode: isEditMode,
    hccCode,
    hccDescription,
    onConditionAction,
    onHccGroupAction,
    onSubmitNote
  } = props

  const hccGroupId = `${conditionGroupTitle.replaceAll(' ', '-').toLowerCase()}-${hccCode}`

  const isActiveHccGroup = (): boolean => (
    hccGroupId === activeHccGroupId
  )

  const onClickHccGroupTitle = () => {
    const { READ } = CONDITION.ACTION
    if (isActiveHccGroup()) {
      onHccGroupAction(
        HCC_GROUP.ACTION.DEACTIVATE,
        hccGroupId
      )
    } else {
      onHccGroupAction(
        HCC_GROUP.ACTION.ACTIVATE,
        hccGroupId
      )
      conditions.forEach(c => {
        onConditionAction(READ, c)
      })
    }
  }

  const onClickConditionTitle = () => {
    // in the hcc group display context, ignore the condition title click
  }

  const hccGroupTitle = () => (
    <CdiConditionHccHeaderTitle
      conditions={ conditions }
      active={ isActiveHccGroup() }
      hccCode={ hccCode }
      hccDescription={ hccDescription }
      onConditionAction={ onConditionAction }
      onEditMode={ isEditMode }
    />
  )

  const renderConditions = () => {
    const isActive = isActiveHccGroup()
    return (
      <div id={ `cdi-encounter-condition-hcc-group-${hccGroupId}` }>
        <Accordion.Title
          key={ hccGroupId }
          active={ isActive }
          onClick={ onClickHccGroupTitle }
        >
          <div>
            { hccGroupTitle() }
          </div>
        </Accordion.Title>
        <Accordion.Content active={ isActive }>
          <Accordion fluid>
            {
              conditions.map((condition, displayIndex) => isEditMode ? (
                <CdiConditionEdit
                  key={ condition.id }
                  currentUser={ currentUser }
                  condition={ condition }
                  encounterDate={ encounterDate }
                  displayIndex={ displayIndex }
                  isActive={ isActive }
                  onClickTitle={ onClickConditionTitle }
                  onSubmitNote={ onSubmitNote }
                  onConditionAction={ onConditionAction }
                  hccChild={ true }
                />
              ) : (
                <CdiConditionRead
                  key={ condition.id }
                  currentUser={ currentUser }
                  condition={ condition }
                  displayIndex={ displayIndex }
                  isActive={ isActive }
                  onClickTitle={ onClickConditionTitle }
                  onSubmitNote={ onSubmitNote }
                  hccChild={ true }
                />
              ))
            }
          </Accordion>
        </Accordion.Content>
      </div>
    )
  }

  return conditions && conditions.length > 0
    ? renderConditions()
    : null
}

export default connect(mapStateToProps)(CdiConditionHccGroup)
